import {
  DeleteOutlined,
  GroupsOutlined,
  Merge,
  Search
} from '@mui/icons-material';
import {
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Typography,
  SxProps
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import Card from '@/components/card';

interface Props {
  isInputDisabled?: boolean;
  hasSelection?: GridRowSelectionModel;
  onChangeSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClickDelete?: () => void;
  onClickGroups?: () => void;
  onClickMerge?: () => void;
  showDeleteButton?: boolean;
  showGroupsButton?: boolean;
  showMergeButton?: boolean;
  showSearchInput?: boolean;
  totalItems?: number;
  searchValue?: string;
  sx?: SxProps;
}

const Toolbar = ({
  isInputDisabled,
  hasSelection = [],
  onChangeSearch,
  onClickDelete,
  onClickGroups,
  onClickMerge,
  showDeleteButton = false,
  showGroupsButton = false,
  showMergeButton = false,
  showSearchInput = false,
  totalItems,
  searchValue = '',
  sx
}: Props) => {
  const showActionButtons =
    showDeleteButton || showSearchInput || showGroupsButton || showMergeButton;

  const hasAnySelection = hasSelection.length > 0;
  const canMerge = hasSelection.length >= 2;

  const iconButtons = [
    {
      show: showGroupsButton,
      onClick: onClickGroups,
      disabled: !hasAnySelection,
      Icon: GroupsOutlined,
      testId: 'toolbar-groups-button',
      ariaLabel: 'Manage groups'
    },
    {
      show: showMergeButton,
      onClick: onClickMerge,
      disabled: !canMerge,
      Icon: Merge,
      testId: 'toolbar-merge-button',
      ariaLabel: 'Merge selected items'
    },
    {
      show: showDeleteButton,
      onClick: onClickDelete,
      disabled: !hasAnySelection,
      Icon: DeleteOutlined,
      testId: 'toolbar-delete-button',
      ariaLabel: 'Delete selected items'
    }
  ] as const;

  return (
    <Card
      dataTestId="toolbar"
      sidePadding={false}
      sx={{ ...sx, backgroundColor: 'transparent' }}
      content={
        <Stack direction="row" justifyContent="flex-start" alignItems="center">
          {!!totalItems && (
            <Typography marginRight="auto">Total: {totalItems}</Typography>
          )}
          {showActionButtons && (
            <Stack direction="row" alignItems="center" ml="auto">
              {iconButtons.map(
                ({ show, onClick, Icon, testId, ariaLabel, disabled }) =>
                  show && (
                    <IconButton
                      key={testId}
                      aria-label={ariaLabel}
                      disabled={disabled}
                      onClick={onClick}
                      size="small"
                      data-testid={testId}
                    >
                      <Icon />
                    </IconButton>
                  )
              )}
              {showSearchInput && (
                <TextField
                  disabled={isInputDisabled}
                  sx={{ backgroundColor: 'white' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search />
                      </InputAdornment>
                    )
                  }}
                  label="Search"
                  onChange={onChangeSearch}
                  value={searchValue}
                  size="small"
                  variant="outlined"
                  data-testid="toolbar-search-input"
                />
              )}
            </Stack>
          )}
        </Stack>
      }
    />
  );
};

export default Toolbar;
